import * as React from 'react'
import { cn } from '@/lib/utils'

export interface ListItemProps extends React.ComponentPropsWithoutRef<'a'> {
    /** Tighter padding and typography for dense menus (e.g. header mega-menu). */
    compact?: boolean
    /** First column: icon or illustration (title + description in the second column). */
    leadingIcon?: React.ReactNode
}

const ListItem = React.forwardRef<React.ElementRef<'a'>, ListItemProps>(({ className, compact, title, children, leadingIcon, ...props }, ref) => {
    const titleClass = cn('font-medium leading-tight', compact ? 'text-xs' : 'text-sm')
    const descClass = cn('text-muted-foreground line-clamp-2', compact ? 'text-[11px] leading-snug' : 'text-sm leading-snug')

    if (leadingIcon) {
        return (
            <li>
                <a
                    ref={ref}
                    className={cn(
                        'group flex select-none items-start gap-3 rounded-md leading-none no-underline outline-none transition-colors',
                        'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
                        compact ? 'p-2' : 'p-3',
                        className,
                    )}
                    {...props}>
                    <div className="shrink-0">{leadingIcon}</div>
                    <div className="min-w-0 flex-1 space-y-0.5 pt-0.5">
                        <div className={titleClass}>{title}</div>
                        <div className={descClass}>{children}</div>
                    </div>
                </a>
            </li>
        )
    }

    return (
        <li>
            <a
                ref={ref}
                className={cn(
                    'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block select-none rounded-md leading-none no-underline outline-none transition-colors',
                    compact ? 'space-y-0.5 p-2' : 'space-y-1 p-3',
                    className,
                )}
                {...props}>
                <div className={titleClass}>{title}</div>
                <div className={descClass}>{children}</div>
            </a>
        </li>
    )
})
ListItem.displayName = 'ListItem'

export { ListItem }
